import { useMemo, type ReactNode } from 'react';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { formatCivilDate, links } from '@eis/core';
import { RequirePermission, StatusChip, type ExportableGridColDef } from '@eis/patterns';
import { insurancePermissions as p } from '../../../permissions';
import type { GroupPolicyRow } from '../model/types';

/**
 * Column definitions for the Gruppen-Policen grid.
 *
 * Dates go through the core formatter, never through string concatenation: an effective date
 * rendered through a timezone is a contract dispute.
 *
 * The two columns that carry a claim about the platform are the partner and the member count.
 * Neither is derived here: both arrive on the row, computed once per page by the service, because
 * a console that derived either would issue one request per row.
 *
 * Each column names its path in `GroupPolicyListEntry`, so the export writes what the grid shows
 * (SDD-020 §9).
 */
export function useGroupPolicyColumns(locale: string): ExportableGridColDef<GroupPolicyRow>[] {
  const { t } = useTranslation('insurance');
  const navigate = useNavigate();

  return useMemo(
    () => [
      {
        field: 'code',
        headerName: t('groupPolicies.columns.name'),
        width: 150,
        exportPath: 'groupPolicy.code',
        // The id is a link (design v2): the row click stays, and the link is what a keyboard,
        // a middle click and "copy link address" reach.
        renderCell: ({ row }): ReactNode =>
          row.code === '' ? (
            '—'
          ) : (
            <Link
              href={links.insurance.groupPolicy(row.code)}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                void navigate({ href: links.insurance.groupPolicy(row.code) });
              }}
            >
              {row.code}
            </Link>
          ),
      },
      {
        field: 'partnerName',
        headerName: t('groupPolicies.columns.partner'),
        flex: 1.4,
        minWidth: 260,
        exportPath: 'partnerName',
        /**
         * An UNRESOLVED partner renders as its id, marked.
         *
         * A blank cell would claim the policyholder has no name; the true claim is that nobody
         * here knows it, because Partner is a domain this platform does not own and no directory
         * is provisioned (SSOT §4.2). The marker is what makes the id readable as an answer
         * rather than as a rendering bug.
         */
        renderCell: ({ row }): ReactNode => (
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
            {/* SDD-014: the name opens the partner for whoever may see the Partner area; text otherwise. */}
            {row.policyholderPartnerId === '' ? (
              <span>{row.partnerName === '' ? '—' : row.partnerName}</span>
            ) : (
              <RequirePermission
                anyOf={[p.partners.view]}
                fallback={<span>{row.partnerName === '' ? '—' : row.partnerName}</span>}
              >
                <Link
                  href={links.partners.partner(row.policyholderPartnerId)}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    void navigate({ href: links.partners.partner(row.policyholderPartnerId) });
                  }}
                >
                  {row.partnerName === '' ? row.policyholderPartnerId : row.partnerName}
                </Link>
              </RequirePermission>
            )}
            {!row.partnerNameResolved && row.partnerName !== '' && (
              <Tooltip title={t('groupPolicies.columns.partnerUnresolvedHint')}>
                <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center' }}>
                  <HelpOutlineIcon fontSize="inherit" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    {t('groupPolicies.columns.partnerUnresolved')}
                  </Typography>
                </Stack>
              </Tooltip>
            )}
          </Stack>
        ),
      },
      {
        field: 'status',
        headerName: t('groupPolicies.columns.status'),
        width: 160,
        exportPath: 'statusName',
        renderCell: (params): ReactNode => (
          <StatusChip
            status={params.row.status}
            label={t(`groupPolicies.status.${params.row.status}` as never)}
          />
        ),
      },
      {
        field: 'activePolicyCount',
        headerName: t('groupPolicies.columns.activePolicies'),
        width: 150,
        exportPath: 'activePolicyCount',
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'validFrom',
        headerName: t('groupPolicies.columns.validFrom'),
        width: 140,
        exportPath: 'snapshot.validFrom',
        exportType: 'DATE',
        valueFormatter: (value: string) => (value === '' ? '—' : formatCivilDate(value, locale)),
      },
    ],
    [t, locale, navigate],
  );
}
